import React, { useState } from "react";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, CheckCircle2, Car, IdCard, Globe, Loader2, Camera, X } from "lucide-react";

const LANGS = ["Português", "Inglês", "Espanhol", "Crioulo", "Francês"];
const AVAIL = ["Integral", "Meio período", "Fins de semana", "Noites", "Flexível"];
const WEEKDAYS = [
  { id: 0, short: "Dom" }, { id: 1, short: "Seg" }, { id: 2, short: "Ter" },
  { id: 3, short: "Qua" }, { id: 4, short: "Qui" }, { id: 5, short: "Sex" },
  { id: 6, short: "Sáb" },
];

export function TalentRegisterForm() {
  const [f, setF] = useState({
    full_name: "", phone: "", email: "", address: "", city: "", state: "",
    zip_code: "", has_license: false, has_car: false, vehicle_type: "",
    languages: [], work_days: [], availability: "", experience: "", photo_path: "",
    travel_radius_miles: 30,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Envie uma imagem");
    if (file.size > 10 * 1024 * 1024) return toast.error("Imagem muito grande (máx. 10MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("photo_path", data.path);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };
  const toggleLang = (l) =>
    setF((p) => ({
      ...p,
      languages: p.languages.includes(l)
        ? p.languages.filter((x) => x !== l)
        : [...p.languages, l],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim()) return toast.error("Informe seu nome completo");
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(f).filter(([, v]) => v !== "" && v != null)
      );
      await api.post("/public/talent-bank", payload);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center" data-testid="talent-register-success">
        <CheckCircle2 className="mx-auto text-emerald-500" size={56} />
        <h2 className="text-2xl font-bold mt-4">Cadastro enviado!</h2>
        <p className="text-slate-600 mt-2">
          Recebemos suas informações. Nossa equipe entrará em contato em breve.
        </p>
      </div>
    );
  }

  return (
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border p-6 space-y-5" data-testid="talent-register-form">
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              {f.photo_path ? (
                <img
                  src={fileUrl(f.photo_path)}
                  alt="Sua foto"
                  className="w-24 h-24 rounded-full object-cover border-2 border-blue-100"
                  data-testid="tr-photo-preview"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                  <Camera size={28} />
                </div>
              )}
              {f.photo_path && (
                <button
                  type="button"
                  onClick={() => set("photo_path", "")}
                  data-testid="tr-photo-remove"
                  className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-white border shadow flex items-center justify-center text-rose-600"
                  aria-label="Remover foto"
                >
                  <X size={15} />
                </button>
              )}
            </div>
            <label className="inline-flex items-center gap-2 px-4 min-h-[40px] rounded-lg border bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium cursor-pointer" data-testid="tr-photo-btn">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
              {uploading ? "Enviando…" : f.photo_path ? "Trocar foto" : "Adicionar foto"}
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="tr-photo-input" disabled={uploading} />
            </label>
            <p className="text-xs text-slate-400">Opcional — ajuda a empresa a te reconhecer</p>
          </div>

          <Field label="Nome completo *">
            <input className="cm-input w-full" data-testid="tr-full-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Telefone">
              <input className="cm-input w-full" data-testid="tr-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
            <Field label="E-mail">
              <input className="cm-input w-full" type="email" data-testid="tr-email" value={f.email} onChange={(e) => set("email", e.target.value)} />
            </Field>
          </div>
          <Field label="Endereço">
            <input className="cm-input w-full" data-testid="tr-address" value={f.address} onChange={(e) => set("address", e.target.value)} />
          </Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Cidade"><input className="cm-input w-full" data-testid="tr-city" value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="Estado"><input className="cm-input w-full" data-testid="tr-state" value={f.state} onChange={(e) => set("state", e.target.value)} /></Field>
            <Field label="CEP"><input className="cm-input w-full" data-testid="tr-zip" value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input type="checkbox" className="w-5 h-5 accent-blue-600" data-testid="tr-has-license" checked={f.has_license} onChange={(e) => set("has_license", e.target.checked)} />
              <IdCard size={18} className="text-slate-500" /> Possui carteira de motorista
            </label>
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input type="checkbox" className="w-5 h-5 accent-blue-600" data-testid="tr-has-car" checked={f.has_car} onChange={(e) => set("has_car", e.target.checked)} />
              <Car size={18} className="text-slate-500" /> Possui carro próprio
            </label>
          </div>
          {f.has_car && (
            <Field label="Tipo de veículo">
              <input className="cm-input w-full" data-testid="tr-vehicle" value={f.vehicle_type} onChange={(e) => set("vehicle_type", e.target.value)} placeholder="Ex.: Sedan, SUV, Van" />
            </Field>
          )}

          <Field label={<span className="flex items-center gap-1.5"><Globe size={15} /> Idiomas</span>}>
            <div className="flex flex-wrap gap-2">
              {LANGS.map((l) => (
                <button type="button" key={l} data-testid={`tr-lang-${l}`} onClick={() => toggleLang(l)}
                  className={`px-3 py-2 rounded-full text-sm border transition-colors ${f.languages.includes(l) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  {l}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Disponibilidade">
            <select className="cm-input w-full" data-testid="tr-availability" value={f.availability} onChange={(e) => set("availability", e.target.value)}>
              <option value="">Selecione…</option>
              {AVAIL.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>

          <Field label="Até onde você está disposto a ir?">
            <div className="flex items-center gap-3">
              <input
                type="range" min="5" max="100" step="5"
                data-testid="tr-radius-range"
                value={f.travel_radius_miles || 30}
                onChange={(e) => set("travel_radius_miles", Number(e.target.value))}
                className="flex-1 accent-blue-600"
              />
              <span className="w-24 text-center text-sm font-semibold text-blue-700 bg-blue-50 rounded-lg py-1.5" data-testid="tr-radius-value">
                {f.travel_radius_miles || 30} mi
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Raio máximo de deslocamento — só aparecerá para clientes dentro dessa distância.</p>
          </Field>

          <Field label="Dias de trabalho na semana">
            <div className="flex flex-wrap gap-2" data-testid="tr-workdays">
              {WEEKDAYS.map((w) => {
                const active = f.work_days.includes(w.id);
                return (
                  <button
                    type="button"
                    key={w.id}
                    data-testid={`tr-wd-${w.id}`}
                    onClick={() =>
                      setF((p) => ({
                        ...p,
                        work_days: p.work_days.includes(w.id)
                          ? p.work_days.filter((x) => x !== w.id)
                          : [...p.work_days, w.id].sort((a, b) => a - b),
                      }))
                    }
                    className={`w-14 h-12 rounded-xl text-sm font-semibold border transition-colors ${
                      active
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                    }`}
                  >
                    {w.short}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-1.5">Toque nos dias em que você pode trabalhar</p>
          </Field>

          <Field label="Experiência">
            <textarea className="cm-input w-full min-h-[90px]" data-testid="tr-experience" value={f.experience} onChange={(e) => set("experience", e.target.value)} placeholder="Conte sua experiência em limpeza / serviços" />
          </Field>

          <button type="submit" disabled={saving} data-testid="tr-submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : <UserPlus size={18} />}
            Enviar cadastro
          </button>
        </form>
  );
}

export default function TalentRegister() {
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center">
            <UserPlus size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Banco de Colaboradores</h1>
            <p className="text-sm text-slate-500">Cadastre-se para futuras oportunidades</p>
          </div>
        </div>
        <TalentRegisterForm />
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    {children}
  </div>
);
